import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2, Lock, Mail, ArrowLeft, Ticket } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#051A0F] grain-overlay relative flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A2818] via-[#051A0F] to-[#051A0F]" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#F0B91B]/15 blur-3xl rounded-full" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#E63946]/10 blur-3xl rounded-full" />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#FDFBF7]/60 hover:text-[#F0B91B] text-sm mb-8 font-outfit transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>

        <div className="rounded-3xl border border-[#F0B91B]/25 bg-[#0A2818]/80 backdrop-blur-md p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#F0B91B] to-[#E5A200] flex items-center justify-center">
              <Ticket className="w-5 h-5 text-[#051A0F]" />
            </div>
            <div>
              <div className="font-bebas text-2xl text-[#F0B91B] tracking-widest leading-none">BETMAR</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FDFBF7]/50 font-outfit">Portal de vendedores</div>
            </div>
          </div>

          <h1 className="font-bebas text-4xl text-[#FDFBF7] tracking-wider mt-6">Iniciar sesión</h1>
          <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-1">Consulta y registra tus ventas de fichas.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold mb-2">Correo</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDFBF7]/40" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@betmar.com"
                  className="w-full bg-[#051A0F] border border-[#F0B91B]/20 rounded-xl pl-10 pr-4 py-3 text-[#FDFBF7] font-outfit placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#F0B91B] transition-colors"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDFBF7]/40" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#051A0F] border border-[#F0B91B]/20 rounded-xl pl-10 pr-4 py-3 text-[#FDFBF7] font-outfit placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#F0B91B] transition-colors"
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-[#E63946]/15 border border-[#E63946]/40 px-4 py-3 text-sm text-[#FDFBF7] font-outfit" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-xl tracking-wider transition-all hover:-translate-y-0.5"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
